import * as React from "react"
import { useForm } from "@inertiajs/react"
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/Card"
import { Button } from "@/components/ui/Button"
import { Input } from "@/components/ui/Input"
import { settings_profile_path } from "@/lib/routes"
import AvatarField from "./AvatarField"

// Inertia types errors as strings, but the controller shares
// `model.errors.messages` (arrays), so accept both shapes.
const fieldError = (msgs: string | string[] | undefined) => {
  if (!msgs) return null
  return (
    <span className="text-xs text-destructive">
      {Array.isArray(msgs) ? msgs.join(", ") : msgs}
    </span>
  )
}

export default function ProfileForm({ user }: {
  user: { name: string | null; email: string; slack_handle: string | null; avatar_url: string | null; avatar_source: string | null }
}) {
  const { data, setData, patch, processing, errors, transform } = useForm({
    name: user.name || "",
    slack_handle: user.slack_handle || "",
  })

  // Controller expects params nested under `user`; data stays flat for input binding.
  transform((data) => ({ user: data }))

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(settings_profile_path(), { preserveScroll: true })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Profile</CardTitle>
        <CardDescription>Your photo, name and contact details.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <AvatarField user={user} />
        <form onSubmit={submit} className="flex flex-col gap-4 max-w-sm">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="profile-name" className="text-sm font-medium">Name</label>
            <Input
              id="profile-name"
              value={data.name}
              onChange={(e) => setData("name", e.target.value)}
              placeholder="Jane Doe"
              disabled={processing}
            />
            {fieldError(errors.name)}
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="profile-slack" className="text-sm font-medium">Slack handle</label>
            <Input
              id="profile-slack"
              value={data.slack_handle}
              onChange={(e) => setData("slack_handle", e.target.value)}
              placeholder="jane.doe"
              disabled={processing}
            />
            {fieldError(errors.slack_handle)}
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">Email</span>
            <span className="text-sm text-muted-foreground select-all">{user.email}</span>
          </div>

          <div>
            <Button type="submit" disabled={processing}>
              {processing ? "Saving…" : "Save"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
